import { useState, useEffect } from 'react';
import { get, orderBy } from 'lodash';
import API_CONSTRUCTOR from '../models/api-model';
import PropertyAPIs from '../models/property';

const apiModel = API_CONSTRUCTOR();
const propertyApi = new PropertyAPIs(apiModel);

// Module-level cache to share data between hook instances
const cache = new Map();
const pendingRequests = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Max distance (in miles) to keep properties — only truly nearby ones make it to the map
const PROXIMITY_RADIUS_MILES = 0.75;

/**
 * Calculate distance between two lat/lng points using the Haversine formula.
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in miles
 */
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Filter properties by proximity to a center point.
 * Only keeps properties within PROXIMITY_RADIUS_MILES of the center.
 * 
 * @param {Array} properties - Array of property objects with latitude/longitude
 * @param {number} centerLat - Center latitude
 * @param {number} centerLng - Center longitude
 * @returns {Array} Filtered properties within radius
 */
const filterByProximity = (properties, centerLat, centerLng) => {
  if (!centerLat || !centerLng || !properties || properties.length === 0) {
    return properties;
  }

  const before = properties.length;
  const filtered = properties.filter((p) => {
    if (!p.latitude || !p.longitude) return false;
    const dist = haversineDistance(centerLat, centerLng, p.latitude, p.longitude);
    return dist <= PROXIMITY_RADIUS_MILES;
  });

  console.log(
    `[useNearbyProperties] Proximity filter (${PROXIMITY_RADIUS_MILES} mi): ${before} → ${filtered.length} properties`
  );

  return filtered;
};

/**
 * Extract street name (without house number) combined with city & state
 * for use as a suggestions API query.
 * 
 * Keeps directional prefixes (N, S, E, W, NE, NW, SE, SW) since they are
 * part of the street identity — especially in grid-based cities like Miami.
 * 
 * Example: "1925 Ne 124th St" + city "North Miami" + state "FL"
 *       → "Ne 124th St North Miami, FL"
 * 
 * @param {Object} property - Property object with address/streetAddress, city, state
 * @returns {string|null} Query string or null if insufficient data
 */
const extractStreetQuery = (property) => {
  const streetAddress = property.streetAddress || property.address;
  const city = property.city;
  const state = property.state;

  if (!streetAddress || !city || !state) return null;

  // Remove house number only (leading digits with optional letter suffix, e.g. "373", "373A", "12-34")
  // Keep directional prefix as it's part of the street identity
  let street = streetAddress
    .replace(/^[\d-]+[A-Za-z]?\s+/, '')
    .trim();

  if (!street) return null;

  return `${street} ${city}, ${state}`;
};

/**
 * Fetch properties by street name using suggestions → search API chain.
 * 
 * 1. Extracts street name from property address (keeps directional prefix)
 * 2. Calls /suggestions to get a structured street suggestion
 * 3. Calls /search with that suggestion as target (limit 100)
 * 
 * Note: Results are later post-filtered by geographic proximity in the main hook.
 * If suggestions API returns no results, this gracefully fails and the other APIs cover.
 * 
 * @param {Object} property - Property object
 * @returns {Promise<Array>} Array of properties
 */
const fetchStreetProperties = async (property) => {
  const streetQuery = extractStreetQuery(property);
  if (!streetQuery) {
    throw new Error('Could not extract street query from property address');
  }

  console.log(`[useNearbyProperties] Street search query: "${streetQuery}"`);

  // Step 1: Get suggestions for the street
  const suggestionsResult = await propertyApi.getSuggestions(encodeURIComponent(streetQuery));
  const suggestions = get(suggestionsResult, 'suggestions', []);

  if (!suggestions || suggestions.length === 0) {
    throw new Error(`No suggestions found for "${streetQuery}"`);
  }

  // Use the first (best match) suggestion
  const bestSuggestion = suggestions[0];
  console.log(`[useNearbyProperties] Using suggestion: "${bestSuggestion.label}" (type: ${bestSuggestion.type})`);

  // Step 2: Search properties using the suggestion as target (100 results for better coverage)
  const searchResult = await propertyApi.getProperties({
    target: bestSuggestion,
    filter: {},
    options: { offset: 0, limit: 2000 },
  });

  const streetProps = get(searchResult, 'properties', []);
  console.log(`[useNearbyProperties] Street search returned ${streetProps.length} properties`);
  return streetProps;
};

/**
 * Custom hook to fetch and manage nearby properties data from multiple sources
 * 
 * Fetches from THREE APIs in parallel for comprehensive results:
 * 1. getNearbyComparables - Returns nearby comparable properties
 * 2. searchSimilarProperties - Returns similar properties by location (100 results)
 * 3. Street-based search - Extracts street name, calls suggestions → search (100 results)
 * 
 * All results are combined, filtered by 2-mile proximity to the current property,
 * intelligently deduplicated (keeping most complete data), and sorted by estimated value.
 * 
 * @param {string} propertyId - The property ID (required for comparables API and filtering)
 * @param {Object} property - Optional full property object with fips/county/zip for search API
 * @param {Object} options - Optional configuration
 * @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
 * @returns {Object} { properties, loading, error, refetch }
 */
export const useNearbyProperties = (propertyId, property = null, options = {}) => {
  const { autoFetch = true } = options;
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNearbyProperties = async () => {
    if (!propertyId) return;

    // Check cache first
    const cacheKey = `nearby-${propertyId}`;
    const cachedData = cache.get(cacheKey);
    
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
      setProperties(cachedData.properties);
      setLoading(false);
      return;
    }

    // Check if there's already a pending request for this propertyId
    if (pendingRequests.has(cacheKey)) {
      // Wait for the existing request to complete
      try {
        const result = await pendingRequests.get(cacheKey);
        setProperties(result);
        setLoading(false);
        return;
      } catch (err) {
        // If pending request failed, we'll make a new one below
      }
    }

    setLoading(true);
    setError(null);
    
    // Create a promise for this request
    const requestPromise = (async () => {
      try {
        console.log('[useNearbyProperties] Fetching from all APIs...');
        
        // Fetch from all THREE APIs in parallel
        const [comparablesResult, searchResult, streetResult] = await Promise.allSettled([
          // 1. Always fetch from comparables API
          propertyApi.getNearbyComparables(propertyId),
          // 2. Fetch from search API if property data available (by zip/county) — 100 results
          property && (property.fips || property.county || property.zip)
            ? propertyApi.searchSimilarProperties(property, { limit: 1000 })
            : Promise.reject(new Error('No property data for search')),
          // 3. Fetch by street name (suggestions → search) for neighbourhood coverage — 100 results
          property && (property.streetAddress || property.address) && property.city && property.state
            ? fetchStreetProperties(property)
            : Promise.reject(new Error('No address data for street search'))
        ]);

        let allProperties = [];

        // Process comparables results
        if (comparablesResult.status === 'fulfilled') {
          const comparablesProps = get(comparablesResult.value, 'properties', []);
          console.log(`[useNearbyProperties] Comparables API returned ${comparablesProps.length} properties`);
          allProperties = [...comparablesProps];
        } else {
          console.warn('[useNearbyProperties] Comparables API failed:', comparablesResult.reason);
        }

        // Process search results
        if (searchResult.status === 'fulfilled') {
          const searchProps = get(searchResult.value, 'properties', []);
          console.log(`[useNearbyProperties] Search API returned ${searchProps.length} properties`);
          allProperties = [...allProperties, ...searchProps];
        } else {
          console.log('[useNearbyProperties] Search API not used or failed:', searchResult.reason?.message);
        }

        // Process street-based search results
        if (streetResult.status === 'fulfilled') {
          const streetProps = streetResult.value || [];
          console.log(`[useNearbyProperties] Street search returned ${streetProps.length} properties`);
          allProperties = [...allProperties, ...streetProps];
        } else {
          console.log('[useNearbyProperties] Street search not used or failed:', streetResult.reason?.message);
        }

        // Check if we got any results
        if (allProperties.length === 0) {
          throw new Error('No properties found from any API');
        }

        console.log(`[useNearbyProperties] Combined total: ${allProperties.length} properties before filtering`);

        // ── Step 1: Exclude the current property ──
        let filtered = allProperties.filter(
          (prop) => prop.id !== propertyId && prop.propertyId !== propertyId
        );

        // ── Step 2: Filter by 2-mile proximity to current property ──
        const centerLat = property?.latitude;
        const centerLng = property?.longitude;
        if (centerLat && centerLng) {
          filtered = filterByProximity(filtered, centerLat, centerLng);
        }

        // ── Step 3: Deduplicate (prefer property with most complete data) ──
        const uniquePropertiesMap = new Map();
        filtered.forEach((prop) => {
          const key = prop.streetAddress || `${prop.latitude}-${prop.longitude}`;
          
          if (!uniquePropertiesMap.has(key)) {
            uniquePropertiesMap.set(key, prop);
          } else {
            const existing = uniquePropertiesMap.get(key);
            const existingScore = (existing.estimatedValue ? 1 : 0) + 
                                 (existing.photoUrl || existing.photoUrls?.length ? 1 : 0) +
                                 (existing.bedrooms ? 1 : 0) +
                                 (existing.bathrooms ? 1 : 0);
            const newScore = (prop.estimatedValue ? 1 : 0) + 
                            (prop.photoUrl || prop.photoUrls?.length ? 1 : 0) +
                            (prop.bedrooms ? 1 : 0) +
                            (prop.bathrooms ? 1 : 0);
            
            if (newScore > existingScore) {
              uniquePropertiesMap.set(key, prop);
            }
          }
        });

        // ── Step 4: Sort by estimated value (descending) ──
        const uniqueProperties = Array.from(uniquePropertiesMap.values());
        const processedProperties = orderBy(
          uniqueProperties,
          ['estimatedValue'],
          ['desc']
        );
        
        console.log(`[useNearbyProperties] Final result: ${processedProperties.length} unique properties (within ${PROXIMITY_RADIUS_MILES} mi)`);

        // Cache the result
        cache.set(cacheKey, {
          properties: processedProperties,
          timestamp: Date.now(),
        });

        return processedProperties;
      } catch (err) {
        console.error('Error fetching nearby properties:', err);
        const error = err.message || 'Failed to fetch nearby properties';
        setError(error);
        throw err;
      } finally {
        // Remove from pending requests
        pendingRequests.delete(cacheKey);
        setLoading(false);
      }
    })();

    // Store the pending request
    pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      setProperties(result);
    } catch (err) {
      setProperties([]);
    }
  };

  useEffect(() => {
    if (autoFetch && propertyId) {
      fetchNearbyProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, property?.fips, autoFetch]);

  return {
    properties,
    loading,
    error,
    refetch: fetchNearbyProperties,
  };
};

/**
 * Clear the cache for a specific property or all properties
 * @param {string} propertyId - Optional property ID to clear cache for. If not provided, clears all cache.
 */
export const clearNearbyPropertiesCache = (propertyId) => {
  if (propertyId) {
    const cacheKey = `nearby-${propertyId}`;
    cache.delete(cacheKey);
    pendingRequests.delete(cacheKey);
  } else {
    cache.clear();
    pendingRequests.clear();
  }
};
